import {
  createAssistantToolContext,
  type AssistantToolContext,
} from '@/features/assistant/tool-context'
import {
  executeAssistantTool,
  getAssistantToolDefinition,
  type AssistantToolAnyDefinition,
} from '@/features/assistant/tool-registry'
import {
  createLocalAgentCancelEvent,
  createLocalAgentLifecycleEvent,
  createLocalAgentProgressEvent,
  createLocalAgentRequestEvent,
  createLocalAgentResponseEvent,
  createLocalAgentSamplingRequestEvent,
  LOCAL_AGENT_EVENT_NAMES,
} from './local-agent-events'
import {
  LOCAL_AGENT_PROTOCOL_VERSION,
  type LocalAgentBridgeCancelRequest,
  type LocalAgentBridgeLifecyclePayload,
  type LocalAgentBridgeProgressPayload,
  type LocalAgentBridgeRequest,
  type LocalAgentBridgeResponse,
  type LocalAgentSamplingResponse,
} from './local-agent-protocol'
import { normalizeAssistantToolError } from '@/features/assistant/tool-errors'
import { deserializeAssistantTransportValue } from '@/features/assistant/transport-value'

type BridgeEventTarget = Pick<
  EventTarget,
  'addEventListener' | 'removeEventListener' | 'dispatchEvent'
>

interface LocalAgentBridgeActiveRequest {
  toolName: string
  controller: AbortController
}

interface LocalAgentBridgePendingSamplingRequest {
  resolve: (response: {
    role: 'assistant'
    content: Array<{
      type: 'text'
      text: string
    }>
  }) => void
  reject: (error: Error) => void
}

export interface LocalAgentBridgeOptions {
  eventTarget?: BridgeEventTarget
  context?: AssistantToolContext
}

function withBridgeRuntimeInput(
  definition: AssistantToolAnyDefinition,
  request: LocalAgentBridgeRequest,
  emitProgress: (payload: LocalAgentBridgeProgressPayload) => void,
  controller: AbortController,
): Record<string, unknown> {
  const input = deserializeAssistantTransportValue({
    ...(request.input ?? {}),
  }) as Record<string, unknown>

  if (definition.supportsProgress) {
    input.onProgress = (detail: unknown) => {
      if (
        detail &&
        typeof detail === 'object' &&
        ('progress' in detail || 'message' in detail || 'phase' in detail)
      ) {
        const candidate = detail as { progress?: unknown; message?: unknown; phase?: unknown }
        emitProgress({
          requestId: request.requestId,
          toolName: request.toolName,
          stage: 'running',
          progressPercent: typeof candidate.progress === 'number' ? candidate.progress : undefined,
          progressLabel:
            typeof candidate.message === 'string'
              ? candidate.message
              : typeof candidate.phase === 'string'
                ? candidate.phase
                : undefined,
          supportsCancellation: definition.supportsCancellation,
          detail,
          protocolVersion: LOCAL_AGENT_PROTOCOL_VERSION,
        })
        return
      }

      if (typeof detail === 'string') {
        emitProgress({
          requestId: request.requestId,
          toolName: request.toolName,
          stage: 'running',
          progressLabel: detail,
          supportsCancellation: definition.supportsCancellation,
          detail,
          protocolVersion: LOCAL_AGENT_PROTOCOL_VERSION,
        })
        return
      }

      emitProgress({
        requestId: request.requestId,
        toolName: request.toolName,
        stage: 'running',
        supportsCancellation: definition.supportsCancellation,
        detail,
        protocolVersion: LOCAL_AGENT_PROTOCOL_VERSION,
      })
    }
  }

  if (definition.supportsCancellation) {
    input.signal = controller.signal
  }

  return input
}

export class LocalAgentBridge {
  private readonly eventTarget: BridgeEventTarget
  private readonly context: AssistantToolContext
  private readonly activeRequests = new Map<string, LocalAgentBridgeActiveRequest>()
  private readonly pendingSamplingRequests = new Map<string, LocalAgentBridgePendingSamplingRequest>()
  private readonly requestListener: EventListener
  private readonly cancelListener: EventListener
  private readonly samplingResponseListener: EventListener

  constructor(options: LocalAgentBridgeOptions = {}) {
    this.eventTarget = options.eventTarget ?? window
    this.context =
      options.context ??
      createAssistantToolContext({
        invocationSource: 'mcp',
        samplingClient: this.createSamplingClient(),
      })
    this.requestListener = ((event: Event) => {
      const detail = (event as CustomEvent<LocalAgentBridgeRequest>).detail
      if (detail) {
        void this.handleRequest(detail)
      }
    }) satisfies EventListener
    this.cancelListener = ((event: Event) => {
      const detail = (event as CustomEvent<LocalAgentBridgeCancelRequest>).detail
      if (detail) {
        this.handleCancel(detail)
      }
    }) satisfies EventListener
    this.samplingResponseListener = ((event: Event) => {
      const detail = (event as CustomEvent<LocalAgentSamplingResponse>).detail
      if (detail) {
        this.handleSamplingResponse(detail)
      }
    }) satisfies EventListener
  }

  connect(): () => void {
    this.eventTarget.addEventListener(LOCAL_AGENT_EVENT_NAMES.request, this.requestListener)
    this.eventTarget.addEventListener(LOCAL_AGENT_EVENT_NAMES.cancel, this.cancelListener)
    this.eventTarget.addEventListener(
      LOCAL_AGENT_EVENT_NAMES.samplingResponse,
      this.samplingResponseListener,
    )

    this.emitLifecycle({
      type: 'app-ready',
      ...this.createLifecycleSnapshot(),
    })
    this.emitLifecycle({
      type: 'workspace-ready',
      ...this.createLifecycleSnapshot(),
    })
    this.emitLifecycle({
      type: 'current-project-changed',
      ...this.createLifecycleSnapshot(),
    })

    return () => this.disconnect()
  }

  disconnect(): void {
    this.eventTarget.removeEventListener(LOCAL_AGENT_EVENT_NAMES.request, this.requestListener)
    this.eventTarget.removeEventListener(LOCAL_AGENT_EVENT_NAMES.cancel, this.cancelListener)
    this.eventTarget.removeEventListener(
      LOCAL_AGENT_EVENT_NAMES.samplingResponse,
      this.samplingResponseListener,
    )

    for (const activeRequest of this.activeRequests.values()) {
      activeRequest.controller.abort()
    }
    this.activeRequests.clear()

    for (const pendingSamplingRequest of this.pendingSamplingRequests.values()) {
      pendingSamplingRequest.reject(new Error('Sampling request was cancelled during disconnect.'))
    }
    this.pendingSamplingRequests.clear()
  }

  dispatchRequest(detail: LocalAgentBridgeRequest): void {
    this.eventTarget.dispatchEvent(createLocalAgentRequestEvent(detail))
  }

  dispatchCancel(detail: LocalAgentBridgeCancelRequest): void {
    this.eventTarget.dispatchEvent(createLocalAgentCancelEvent(detail))
  }

  emitCurrentProjectChanged(): void {
    this.emitLifecycle({
      type: 'current-project-changed',
      ...this.createLifecycleSnapshot(),
    })
  }

  emitWorkspaceReady(): void {
    this.emitLifecycle({
      type: 'workspace-ready',
      ...this.createLifecycleSnapshot(),
    })
  }

  private createLifecycleSnapshot(): Omit<
    LocalAgentBridgeLifecyclePayload,
    'type' | 'protocolVersion'
  > {
    return {
      currentProjectId: this.context.currentProjectId,
      currentWorkspaceName: this.context.currentWorkspaceName,
      currentEditorWorkspace: this.context.currentEditorWorkspace,
      isUserSignedIn: this.context.isUserSignedIn,
      subscriptionTier: this.context.subscriptionTier,
    }
  }

  private emitLifecycle(payload: Omit<LocalAgentBridgeLifecyclePayload, 'protocolVersion'>): void {
    this.eventTarget.dispatchEvent(
      createLocalAgentLifecycleEvent({
        ...payload,
        protocolVersion: LOCAL_AGENT_PROTOCOL_VERSION,
      }),
    )
  }

  private emitResponse(detail: LocalAgentBridgeResponse): void {
    this.eventTarget.dispatchEvent(createLocalAgentResponseEvent(detail))
  }

  private emitProgress(detail: LocalAgentBridgeProgressPayload): void {
    this.eventTarget.dispatchEvent(createLocalAgentProgressEvent(detail))
  }

  private createSamplingClient(): AssistantToolContext['samplingClient'] {
    return {
      createMessage: async (params) => {
        const requestId = crypto.randomUUID()

        return await new Promise((resolve, reject) => {
          this.pendingSamplingRequests.set(requestId, { resolve, reject })
          this.eventTarget.dispatchEvent(
            createLocalAgentSamplingRequestEvent({
              requestId,
              messages: params.messages,
              systemPrompt: params.systemPrompt,
              temperature: params.temperature,
              maxTokens: params.maxTokens,
              protocolVersion: LOCAL_AGENT_PROTOCOL_VERSION,
            }),
          )
        })
      },
    }
  }

  private handleSamplingResponse(response: LocalAgentSamplingResponse): void {
    const pendingSamplingRequest = this.pendingSamplingRequests.get(response.requestId)
    if (!pendingSamplingRequest) {
      return
    }

    this.pendingSamplingRequests.delete(response.requestId)
    if (response.ok) {
      pendingSamplingRequest.resolve(response.result)
      return
    }

    pendingSamplingRequest.reject(new Error(response.error.message))
  }

  private handleCancel(request: LocalAgentBridgeCancelRequest): void {
    const activeRequest = this.activeRequests.get(request.requestId)
    if (!activeRequest) {
      return
    }

    activeRequest.controller.abort(request.reason)
    this.activeRequests.delete(request.requestId)
  }

  private async handleRequest(request: LocalAgentBridgeRequest): Promise<void> {
    const definition = getAssistantToolDefinition(request.toolName)
    if (!definition) {
      const error = normalizeAssistantToolError(
        new Error(`Unknown assistant tool: ${request.toolName}`),
      )
      this.emitResponse({
        requestId: request.requestId,
        toolName: request.toolName,
        ok: false,
        error: error.toJSON(),
        protocolVersion: LOCAL_AGENT_PROTOCOL_VERSION,
      })
      return
    }

    const controller = new AbortController()
    this.activeRequests.set(request.requestId, {
      toolName: request.toolName,
      controller,
    })

    try {
      if (definition.supportsProgress) {
        this.emitProgress({
          requestId: request.requestId,
          toolName: request.toolName,
          stage: 'starting',
          progressPercent: 0,
          progressLabel: 'starting',
          supportsCancellation: definition.supportsCancellation,
          protocolVersion: LOCAL_AGENT_PROTOCOL_VERSION,
        })
      }

      const result = await executeAssistantTool(
        request.toolName,
        withBridgeRuntimeInput(
          definition,
          request,
          (payload) => this.emitProgress(payload),
          controller,
        ),
        this.context,
      )

      if (definition.supportsProgress) {
        this.emitProgress({
          requestId: request.requestId,
          toolName: request.toolName,
          stage: 'completed',
          progressPercent: 100,
          progressLabel: 'completed',
          supportsCancellation: definition.supportsCancellation,
          protocolVersion: LOCAL_AGENT_PROTOCOL_VERSION,
        })
      }

      this.emitResponse({
        requestId: request.requestId,
        toolName: request.toolName,
        ok: true,
        result,
        protocolVersion: LOCAL_AGENT_PROTOCOL_VERSION,
      })
    } catch (error) {
      const normalizedError = normalizeAssistantToolError(error)
      if (definition.supportsProgress) {
        this.emitProgress({
          requestId: request.requestId,
          toolName: request.toolName,
          stage: normalizedError.code === 'TOOL_CANCELLED' ? 'cancelled' : 'failed',
          progressLabel:
            normalizedError.code === 'TOOL_CANCELLED' ? 'cancelled' : normalizedError.message,
          supportsCancellation: definition.supportsCancellation,
          detail: normalizedError.toJSON(),
          protocolVersion: LOCAL_AGENT_PROTOCOL_VERSION,
        })
      }
      this.emitResponse({
        requestId: request.requestId,
        toolName: request.toolName,
        ok: false,
        error: normalizedError.toJSON(),
        protocolVersion: LOCAL_AGENT_PROTOCOL_VERSION,
      })
    } finally {
      this.activeRequests.delete(request.requestId)
    }
  }
}

export function createLocalAgentBridge(options: LocalAgentBridgeOptions = {}): LocalAgentBridge {
  return new LocalAgentBridge(options)
}
